import {Component, OnInit, ViewChild, OnDestroy} from '@angular/core';
import * as Chart from 'chart.js';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {FeedRateService} from 'src/app/services/feedRate.service';
import FeedRateModel from 'src/app/services/feedRate.model';
import {PondSectionService} from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component ProjectedFeedGraphComponent
 * @selector app-projected-feed-graph
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `ProjectedFeedGraphComponent` is responsible for visualizing the comparison between actual and projected feed consumption
 * for aquaculture ponds. It generates a line chart that plots cumulative feed usage against Days of Culture (DOC),
 * providing valuable insights for feed management and cost optimization.
 *
 * ## Key Responsibilities:
 * 1. **Data Visualization**: Renders a Chart.js line chart comparing 'Actual Cumulative Feed' vs 'Projected Cumulative Feed' over time.
 * 2. **Hierarchical Selection**: Manages a dependent dropdown chain for selecting Business -> Farm Site -> Section -> Pond.
 * 3. **Data Retrieval**: Fetches feed rate data (actual and projected) based on the selected pond and species type.
 * 4. **Pagination**: Implements client-side pagination for the chart data to handle long culture durations effectively.
 * 5. **Role-Based Access**: Adapts initialization and data availability based on the logged-in user's role (Farmer, Farm Manager, Business Admin).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to all farm sites within their business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them (via Farm Spend context).
 * - **Other Categories**:
 *   - **Employee**: Can search for and select a business entity to view reports.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context ('Farm').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 * - `LoginUserName`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial Farm Sites or Business Entities.
 * 2. **Selection**: User selects a Farm Site -> Section -> Pond.
 * 3. **Data Fetching**: `formsubmit` triggers `FeedRateService.getProjectedFeedRate` to fetch comparison data.
 * 4. **Processing**: Data is processed into arrays for chart labels (DOC) and datasets (Actual Feed, Projected Feed).
 * 5. **Pagination**: `onPagination` slices the data arrays to display a subset of days on the chart, with Next/Prev controls.
 * 6. **Rendering**: `setupCDFChart` renders the Chart.js instance with the processed data.
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad` manages loading indicators.
 * - **Form**: `registerForm` handles selection criteria and validation.
 * - **Chart**: Manages Chart.js instance `lineChart`.
 */
@Component({
  selector: 'app-projected-feed-graph',
  templateUrl: './projected-feed-graph.component.html',
  styleUrls: ['./projected-feed-graph.component.scss']
})
export class ProjectedFeedGraphComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntites: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmsites: Array<FarmSiteModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds for the selected section. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<PondSectionModel>} List of sections within the selected farm site. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<FeedRateModel>} List of feed rate records fetched from the service. */
  public feedRates: Array<FeedRateModel>;

  /** @type {FeedRateModel} Model used for binding filter criteria. */
  public feedRate: FeedRateModel;

  /** @type {boolean} Flag for page loading state. */
  pgLoad = false;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag indicating if the chart should be displayed. */
  public isChartDisplay: boolean;

  /** @type {any[]} Dropdown list for active pond details. */
  public activepondsDetails_dropdown: any[];

  /** @type {any[]} Array of actual cumulative feed values. */
  public actualCumulativeFeed: any[];

  /** @type {any[]} Array of actual cumulative DOC values. */
  public actualCumulativeDOC: any[];

  /** @type {any[]} Array of projected cumulative feed values. */
  public projectedCumulativeFeed: any[];

  /** @type {any[]} Array of projected cumulative DOC values. */
  public projectedCumulativeDOC: any[];

  /** @type {any[]} Array of cumulative feed objects. */
  public cumulativeFeedObjs: any[];

  /** @type {any[]} Response data from the service. */
  public response: any[];

  /** @type {number} Pagination offset for chart data. Default 0. */
  paginationOffset: number = 0;

  /** @type {number} Pagination limit for chart data. Default 20. */
  public limit: number = 20;

  /** @type {any[]} Paginated actual cumulative feed values. */
  public actualPaginationCumulativeFeed: any = [];

  /** @type {any[]} Paginated actual cumulative DOC values. */
  public actualPaginationCumulativeDOC: any = [];

  /** @type {any[]} Paginated projected cumulative feed values. */
  public projectedPaginationCumulativeFeed: any = [];

  /** @type {any[]} Paginated cumulative feed objects. */
  public paginatedCumulativeFeedObjs: any = [];

  /** @type {any} Chart.js instance for the line chart. */
  public lineChart: any;

  /** @type {boolean} Flag indicating if search button has been clicked. */
  public isSearchClicked: boolean;

  /** @type {any} ViewChild reference for line canvas (unused). */
  @ViewChild('lineCanvas') lineCanvas;

  /** @type {any} ViewChild reference for cumulative feed comparison canvas. */
  @ViewChild('cumulativeFeedComparisionCanvas') cumulativeFeedComparisionCanvas;

  /** @type {number} Number of days in the culture cycle. */
  public noOfDays: number;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {FormGroup} Main reactive form for filter criteria. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag for dropdown visibility. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for business dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Business search input placeholder/value. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if form submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag to disable submit button during processing. */
  public submitButtonDisabled: boolean;

  /**
   * Constructor for ProjectedFeedGraphComponent.
   * Initializes services and the reactive form.
   *
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {FeedRateService} feedRateService - Service for fetching feed rate data.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {PondSectionService} pondSectionService - Service for pond section operations.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service FormBuilder
   * @service FeedRateService
   * @service BusinessEntityService
   * @service ActivatedRoute
   * @service FarmSiteService
   * @service CustomerPondService
   * @service PondSectionService
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public feedRateService: FeedRateService, public businessEntityService: BusinessEntityService, public route: ActivatedRoute, public farmSiteService: FarmSiteService, public customerPondService: CustomerPondService, public pondSectionService: PondSectionService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.feedRate = new FeedRateModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required],
      sectionField: ['', Validators.required],
      pondField: ['', Validators.required],
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType');
      /* user login details */
      this.keyword = 'businessName';
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      /* user login details end */
      this.isSearchClicked = false; // if data is not available
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.isSearchClicked = false;
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmIdInFarmSpend(localStorage.businessID);
        this.pgLoad = true;
        this.setSingleValueForFormLocation();
        this.feedRate.business_id = localStorage.businessID;
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.isSearchClicked = false;
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.pgLoad = true;
        this.setSingleValueForFormLocation();
        this.feedRate.business_id = localStorage.businessID;
      } else {
        this.isSearchClicked = false;
        this.businessSearch = undefined;
        this.registerForm.get('businessfield').patchValue('');
        let farmBusiness = await this.businessEntityService.getAllBusiness();
        this.businessEntites = farmBusiness.filter(businessEntity => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID
          === '618916f2b4391c6d48137e3d');
        this.filteredOptions = this.businessEntites;
        this.pgLoad = true;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businessEntites` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntites.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @description Handles the selection of a business entity from the dropdown.
   * Updates the form value and triggers fetching of farm sites.
   * @param {BusinessEntity} business - The selected business entity.
   * @side-effect Updates `registerForm`, `isDropdownOpen`, `feedRate.business_id`, calls `getAllFarmsitesBybusinessId`.
   */
  public selectOption(business: BusinessEntity): void {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.feedRate.business_id = business.id;
    if (this.feedRate.business_id) {
      this.getAllFarmsitesBybusinessId();
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Fetches farm sites for the selected business ID (Admin/Employee flow).
   */
  public async getAllFarmsitesBybusinessId() {
    this.feedRate.farm_site_id = undefined;
    this.farmsites = [];
    this.isChartDisplay = false;
    this.feedRate.pond_id = undefined;
    this.feedRate.section_id = undefined;
    this.activepondsDetails_dropdown = [];
    this.isSearchClicked = false;
    try {
      this.farmsites = await this.farmSiteService.getFarmeSitesByFarmId(this.feedRate.business_id);
      this.pgLoad = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one exists and triggers section fetching.
   * @side-effect Updates `feedRate.farm_site_id`.
   */
  public setSingleValueForFormLocation() {
    if (this.farmsites && this.farmsites.length == 1) {
      this.feedRate.farm_site_id = this.farmsites[0].id; // model-binding key
      this.getSetionsByFarmsiteId(this.feedRate.farm_site_id);
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Fetches sections associated with a specific farm site ID.
   * Auto-selects the section if only one exists.
   * @param {string} farmsiteid - The ID of the farm site.
   */
  public async getSetionsByFarmsiteId(farmsiteid: string) {
    try {
      this.feedRate.section_id = undefined;
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteid);
      this.setSingleValueForSection();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForSection
   * @description Auto-selects the section if only one exists and triggers pond fetching.
   * @side-effect Updates `feedRate.section_id`.
   */
  public setSingleValueForSection() {
    if (this.pondSections && this.pondSections.length == 1) {
      this.feedRate.section_id = Number(this.pondSections[0].id); // model-binding key
      this.getPondsBySectionId(this.feedRate.section_id);
    }
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Fetches ponds associated with a specific section ID.
   * Populates `activepondsDetails_dropdown` with active ponds.
   * @param {number} section_id - The ID of the section.
   */
  public async getPondsBySectionId(section_id: number) {
    this.isSearchClicked = false;
    this.activepondsDetails_dropdown = [];
    try {
      this.customerPonds = await this.customerPondService.getAllPondsBySectionId(section_id);
      this.activepondsDetails_dropdown = [];
      if (this.customerPonds && this.customerPonds.length > 0) {
        for (let i = 0; i < this.customerPonds.length; i++) {
          let eachrec = this.customerPonds[i];
          if (eachrec.isPreparatory == false && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
            for (let j = 0; j < eachrec.PondCultureData.length; j++) {
              let eachpondculture = eachrec.PondCultureData[j];
              eachpondculture.pond_name = eachrec.name;
              eachpondculture.pond_id = eachrec.id;
              this.activepondsDetails_dropdown.push(eachpondculture);
            }
          }
        }
      }
      this.setSingleValueForPond();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForPond
   * @description Auto-selects the pond if only one exists.
   * @side-effect Updates `feedRate.pond_id`.
   */
  public setSingleValueForPond() {
    if (this.activepondsDetails_dropdown && this.activepondsDetails_dropdown.length == 1) {
      this.feedRate.pond_id = this.activepondsDetails_dropdown[0].pond_id; // model-binding key
    }
  }

  /**
   * @method formsubmit
   * @async
   * @description Handles form submission to generate the projected feed graph.
   * Validates form, fetches projected feed rate data, processes response, and initializes pagination.
   * @side-effect Updates `feedRates`, `actualCumulativeFeed`, `projectedCumulativeFeed`, `noOfDays`.
   */
  public async formsubmit() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.isSearchClicked = false;
      this.submitButtonDisabled = true;
      const item = this.activepondsDetails_dropdown.find(pondId => pondId && pondId.pond_id == this.feedRate.pond_id);
      this.feedRates = await this.feedRateService.getProjectedFeedRate(item.id, item.pond_id, item.species_type_id);
      this.submitButtonDisabled = false;
      this.response = this.feedRates;
      let eachObj = this.response[0];
      let date1 = new Date(eachObj[0].startDate.split('T')[0]);
      let date2 = new Date();
      let Difference_In_Time = date2.getTime() - date1.getTime();
      let diffDays = Math.ceil(Difference_In_Time / (1000 * 60 * 60 * 24)) + 1;
      this.noOfDays = diffDays;
      this.isSearchClicked = true;
      this.actualCumulativeFeed = [];
      this.actualCumulativeDOC = [];
      this.projectedCumulativeFeed = [];
      this.projectedCumulativeDOC = [];
      this.cumulativeFeedObjs = [];
      this.paginationOffset = 0;
      this.submitted = false;
      this.limit = 20;
      for (let i = 0; i <= this.response.length - 1; i++) {
        for (let j = 0; j <= this.response[i].length - 1; j++) {
          if (this.feedRates[i][j]['actualCumulativeFeed'] != -1 && this.feedRates[i][j]['actualCumulativeFeed'] !== null) {
            this.actualCumulativeFeed.push(parseFloat((this.feedRates[i][j]['actualCumulativeFeed'] / 1000).toFixed(2)));
          } else {
            this.actualCumulativeFeed.push(null);
          }
          this.actualCumulativeDOC.push(this.feedRates[i][j]['projectedDOC']);
          this.cumulativeFeedObjs.push(this.feedRates[i][j]);
          this.projectedCumulativeFeed.push(this.feedRates[i][j]['projectedCumulativeFeed']);
          this.projectedCumulativeDOC.push(this.feedRates[i][j]['projectedDOC']);
        }
      }
      this.onPagination();
      this.onseachdispCurrentDate();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method onPagination
   * @description Handles pagination logic for the chart data.
   * Slices the data arrays based on `paginationOffset` and `limit`.
   * Calls `setupCDFChart` to render the chart with the sliced data.
   */
  public onPagination = () => {
    this.actualPaginationCumulativeFeed = [];
    this.actualPaginationCumulativeDOC = [];
    this.projectedPaginationCumulativeFeed = [];
    this.paginatedCumulativeFeedObjs = [];
    for (let i = this.paginationOffset - 1; i <= this.limit - 1; i++) {
      if (this.actualCumulativeDOC[i] != undefined) {
        this.actualPaginationCumulativeFeed.push(this.actualCumulativeFeed[i]);
        this.actualPaginationCumulativeDOC.push(this.actualCumulativeDOC[i]);
        this.projectedPaginationCumulativeFeed.push(this.projectedCumulativeFeed[i]);
      }
    }
    for (let i = this.paginationOffset - 1; i <= this.limit - 1; i++) {
      if (this.cumulativeFeedObjs[i]) {
        this.paginatedCumulativeFeedObjs.push(this.cumulativeFeedObjs[i]);
      }
    }
    this.setupCDFChart(this.actualPaginationCumulativeFeed, this.actualPaginationCumulativeDOC, this.actualPaginationCumulativeDOC);
  };

  /**
   * @method getPrevComparisionData
   * @description Loads the previous batch of data for the chart.
   * Updates `paginationOffset` and `limit`.
   */
  public getPrevComparisionData = () => {
    this.paginationOffset -= 21;
    this.limit -= 21;
    this.onPagination();
  };

  /**
   * @method getNextComparisionData
   * @description Loads the next batch of data for the chart.
   * Updates `paginationOffset` and `limit`.
   */
  public getNextComparisionData = () => {
    this.paginationOffset += 21;
    this.limit += 21;
    this.onPagination();
  };

  /**
   * @method onseachdispCurrentDate
   * @description Automatically navigates pagination to the current date/DOC range on load.
   */
  public onseachdispCurrentDate() {
    let noOfclicks = 0;
    let days = this.noOfDays;
    if (this.noOfDays > 150) {
      days = 150;
    }
    if (days > 20) {
      const daysCalculation = days / 20;
      if (daysCalculation.toString().includes('.')) {
        noOfclicks = parseInt('' + days / 20, 10);
      } else {
        noOfclicks = daysCalculation - 1;
      }
      for (let i = 0; i < noOfclicks; i++) {
        $('#nextPagination').click();
      }
    }
  }

  /**
   * @method setupCDFChart
   * @description Initializes and renders the Chart.js line chart for cumulative feed comparison.
   * Configures datasets for Actual and Projected feed.
   * @param {any[]} actualCumulativeFeed - Data for actual feed.
   * @param {any[]} projectedCumulativeFeed - Data for projected feed.
   * @param {any[]} projectedCumulativeDOC - Labels (DOC) for the chart.
   */
  public setupCDFChart(actualCumulativeFeed, projectedCumulativeFeed, projectedCumulativeDOC) {
    const ctx = this.cumulativeFeedComparisionCanvas.nativeElement.getContext('2d');
    let gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, '#0f4573');
    gradient.addColorStop(0.5, '#51b7c5');
    this.lineChart = new Chart(this.cumulativeFeedComparisionCanvas.nativeElement, {
      type: 'line',
      data: {
        labels: projectedCumulativeDOC,
        datasets: [
          {
            fill: false,
            spanGaps: true,
            lineTension: 0.5,
            // backgroundColor: gradient,
            borderColor: '#b80d2f',
            borderCapStyle: 'butt',
            borderDash: [],
            borderDashOffset: 0.0,
            borderJoinStyle: 'miter',
            pointBorderColor: '#b80d2f',
            pointBackgroundColor: '#fff',
            pointBorderWidth: 5,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#b80d2f',
            pointHoverBorderColor: '#b80d2f',
            pointHoverBorderWidth: 2,
            pointRadius: 1,
            pointHitRadius: 10,
            data: actualCumulativeFeed,
            borderWidth: 0.9
          },
          {
            spanGaps: true,
            fill: false,
            lineTension: 0.5,
            // backgroundColor: gradient,
            borderColor: '#0f4573',
            borderCapStyle: 'butt',
            borderDash: [],
            borderDashOffset: 0.0,
            borderJoinStyle: 'miter',
            pointBorderColor: 'rgba(75,192,192,1)',
            pointBackgroundColor: '#fff',
            pointBorderWidth: 5,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: 'rgba(75,192,192,1)',
            pointHoverBorderColor: 'rgba(220,220,220,1)',
            pointHoverBorderWidth: 2,
            pointRadius: 1,
            pointHitRadius: 10,
            data: projectedCumulativeFeed,
            //spanGaps: false,
            borderWidth: 0.9
          }
        ]
      },
      options: {
        legend: {
          display: false
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Feed'
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        //hover: {mode: null},
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function() {
              return null;
            },
            label: (tooltipItem: any) => {
              let dataPointABW = '';
              if (this.paginatedCumulativeFeedObjs[tooltipItem['index']]) {
                if (tooltipItem['datasetIndex'] == 0) {
                  dataPointABW = ', ABW:' + this.paginatedCumulativeFeedObjs[tooltipItem['index']]['actualABW'] + ' Gram(s)';
                } else {
                  dataPointABW = ', ABW:' + this.paginatedCumulativeFeedObjs[tooltipItem['index']]['projectedABW'] + ' Gram(s)';
                }
              }
              return 'Doc:' + tooltipItem.xLabel + ' ,Feed:' + tooltipItem.yLabel + ' Kg(s)' + '' + dataPointABW;
            }
          }
        }
      }
    });
  }
}
